import React, { useState } from 'react';
import { Col } from 'reactstrap';
import ViewOriginModal from '../../pages/Procurement/component/ViewOriginModal';

const isFilled = value =>
  value !== null && value !== undefined && String(value).trim() !== '';

const buildAldSchedule = (timelineType, startDate, endDate) => {
  const dateRange = [startDate, endDate].filter(isFilled).join(' - ');
  return [timelineType, dateRange].filter(isFilled).join(' ');
};

const buildDeliveryText = ({
  incoterm,
  incotermPortType,
  location,
  timelineType,
  startDate,
  endDate,
}) => {
  const prefix = [incoterm, incotermPortType, location]
    .filter(isFilled)
    .join(' ');
  const schedule = buildAldSchedule(timelineType, startDate, endDate);

  if (!prefix) return schedule || '-';
  if (!schedule) return prefix;
  return `${prefix}, on ${schedule}`;
};

const buildFobDeliveryText = ({
  incoterm,
  incotermPortType,
  location,
  timelineType,
  startDate,
  endDate,
}) => {
  const prefix = [incoterm, incotermPortType, location]
    .filter(isFilled)
    .join(' ');
  const schedule = buildAldSchedule(timelineType, startDate, endDate);

  if (!prefix) return schedule || '-';
  if (!schedule) return prefix;
  return `${prefix}, ${schedule}`;
};

const getFobLocationNames = data =>
  (Array.isArray(data.incotermFOBLocations) ? data.incotermFOBLocations : [])
    .map(location => location?.name ?? location?.Name ?? location)
    .filter(isFilled);

const isFobOriginPortType = portType =>
  String(portType || '')
    .trim()
    .toLowerCase() === 'origin';

const normalizeFobLocations = locations =>
  (Array.isArray(locations) ? locations : [])
    .map(location => ({
      name: location?.name ?? location?.Name ?? String(location ?? ''),
    }))
    .filter(location => isFilled(location.name));

const buildFlatIncoterms = (items = []) =>
  (Array.isArray(items) ? items : []).flatMap((data, index) => {
    const fobLocationNames = getFobLocationNames(data);

    if (fobLocationNames.length > 0) {
      const isOrigin =
        isFobOriginPortType(data.incotermPortType) ||
        fobLocationNames.length > 2;

      const locationLabel = isOrigin
        ? 'All Origin'
        : fobLocationNames.join(' or ');

      return [
        {
          text: buildFobDeliveryText({
            incoterm: data.incoterm,
            incotermPortType: isOrigin ? null : data.incotermPortType,
            location: locationLabel,
            timelineType: data.timelineType,
            startDate: data.startDate,
            endDate: data.endDate,
          }),
          className: '',
          showOriginLink: isOrigin,
          fobLocations: isOrigin ? data.incotermFOBLocations : null,
          sourceIndex: index,
          rowKey: `fob-${index}`,
        },
      ];
    }

    const rows = [
      {
        text: buildDeliveryText({
          incoterm: data.incoterm,
          incotermPortType: data.incotermPortType,
          location: data.incotermLocation,
          timelineType: data.timelineType,
          startDate: data.startDate,
          endDate: data.endDate,
        }),
        className: '',
        showOriginLink: false,
        sourceIndex: index,
        rowKey: `primary-${index}`,
      },
    ];

    if (isFilled(data.incotermLocation2)) {
      rows.push({
        text: buildDeliveryText({
          incoterm: data.incoterm,
          incotermPortType: data.incotermPortType,
          location: data.incotermLocation2,
          timelineType: data.timelineType,
          startDate: data.startDate2,
          endDate: data.endDate2,
        }),
        className: 'bg-light bg-opacity-50',
        showOriginLink: false,
        sourceIndex: index,
        rowKey: `secondary-${index}`,
      });
    }

    if (isFilled(data.incotermLocation3)) {
      rows.push({
        text: buildDeliveryText({
          incoterm: data.incoterm,
          incotermPortType: data.incotermPortType,
          location: data.incotermLocation3,
          timelineType: data.timelineType,
          startDate: data.startDate3,
          endDate: data.endDate3,
        }),
        className: 'bg-light bg-opacity-50',
        showOriginLink: false,
        sourceIndex: index,
        rowKey: `tertiary-${index}`,
      });
    }

    return rows.filter(row => row.text !== '-');
  });

const IncotermTableFragment = ({ items = [] }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLocations, setModalLocations] = useState([]);
  const flatIncoterms = buildFlatIncoterms(items);

  const handleShowOrigin = locations => {
    setModalLocations(normalizeFobLocations(locations));
    setModalOpen(true);
  };

  return (
    <Col md={8} className='ps-md-4'>
      <div className='text-muted small fw-semibold mb-2'>Incoterm</div>
      <div className='border rounded shadow-none bg-white'>
        <div className='bg-light p-2 border-bottom text-muted small fw-medium fs-12'>
          Delivery
        </div>

        {flatIncoterms.length > 0 ? (
          flatIncoterms.map((row, index) => (
            <div
              key={row.rowKey}
              className={`p-2 d-flex justify-content-between align-items-center ${row.className} ${
                index !== flatIncoterms.length - 1 ? 'border-bottom' : ''
              }`}
            >
              <span className='text-dark fs-13'>{row.text}</span>
              {row.showOriginLink && (
                <button
                  type='button'
                  className='text-primary text-decoration-none fw-medium fs-13'
                  onClick={() => handleShowOrigin(row.fobLocations)}
                  style={{ border: 0, background: 'transparent' }}
                >
                  Show All
                </button>
              )}
            </div>
          ))
        ) : (
          <div className='text-center text-muted p-3 fs-13'>
            No Data Available
          </div>
        )}
      </div>
      <ViewOriginModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        locations={modalLocations}
      />
    </Col>
  );
};

export default IncotermTableFragment;
